import { defineStore } from 'pinia';
import { useFetch, useCookie } from '#app';
import {
  computed,
  ref,
  useRuntimeConfig,
  navigateTo,
  nextTick,
  showError,
} from '#imports';
import { apiFetch, fatalErrorHandler } from '~/utils/apiFetch.js';
import {
  runLogoutFlow,
  generatePkcePair,
  generateState,
  usePermissionChecker,
  hasRole,
  hasMatch,
} from 'r3ui-lib';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const cookie = useCookie('auth_token');
    const token = ref(cookie.value);
    const user = ref(null);
    const isLoading = ref(false);

    const isAuthenticated = computed(() => {
      return Boolean(token.value);
    });

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map(role => role?.name)
        .filter(roleName => Boolean(roleName));
    });

    const hasUserRole = role => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }

      return hasRole(userRoleNames.value, role);
    };

    // SSO Login функция
    const signInWithSSO = async () => {
      isLoading.value = true;

      try {
        // eslint-disable-next-line no-console
        console.log('Starting SSO login...');

        // Получаем конфигурацию
        const config = useRuntimeConfig();
        const authApiUrl = config.public.authApiUrl; // URL для OAuth авторизации
        const ssoClientId = config.public.ssoClientId;

        // Проверяем наличие необходимых значений
        if (!authApiUrl || !ssoClientId) {
          throw new Error('Не настроены CLIENT_ID или NUXT_AUTH_API_URL');
        }

        let codeVerifier;
        let codeChallenge;
        let state;
        try {
          ({ codeVerifier, codeChallenge } = await generatePkcePair());
          state = generateState();
        } catch (error) {
          isLoading.value = false;
          showError({
            statusCode: 500,
            statusMessage:
              'Web Crypto API недоступен. Используйте HTTPS или обновите браузер.',
          });
          return;
        }
        const pkceCookie = useCookie('pkce_verifier', {
          maxAge: 300,
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
          path: '/',
        });
        pkceCookie.value = codeVerifier;
        const stateCookie = useCookie('pkce_state', {
          maxAge: 300,
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
          path: '/',
        });
        stateCookie.value = state;

        // Строим URL для перенаправления на сервер
        const redirectUri = `${config.public.origin}/api/auth/callback`;
        const authUrl =
          `${authApiUrl}/oauth/authorize?` +
          new URLSearchParams({
            client_id: ssoClientId,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: '*',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state,
          });

        // eslint-disable-next-line no-console
        console.log('Redirecting to:', authUrl);

        // Перенаправляем на сервер
        window.location.href = authUrl;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('SSO login error:', e);
        isLoading.value = false;
      }
    };

    async function fetchUser() {
      if (!token.value) return false;
      // This request will be proxied by the rule in nuxt.config.ts
      const { data, error } = await useFetch('/api/services/auth/user', {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
        onResponse({ response }) {
          fatalErrorHandler(response);
        },
      });
      if (error.value) {
        // Token might be invalid, clear it
        // logout();
        // return false;
      } else {
        user.value = data.value;
      }
    }

    function resetStorage() {
      const storageLinks = ['breadcrumbs', 'filters', 'tables'];
      storageLinks.map(i => {
        localStorage.removeItem(i);
      });
    }

    async function logOutSSO() {
      const config = useRuntimeConfig();
      const url = `${config.public.authApiUrl}/api/logout`;

      return apiFetch(url, {
        method: 'POST',
      });
    }

    function resetAll() {
      token.value = null;
      user.value = null;
      cookie.value = null;
    }

    async function logout() {
      console.log('Logout click: start');
      const config = useRuntimeConfig();
      const authApiUrl = config.public.authApiUrl;
      const origin = config.public.origin;
      const baseLogoutUrl = authApiUrl ? `${authApiUrl}/logout` : '/login';
      const redirectPath = origin
        ? `${baseLogoutUrl}?redirect=${encodeURIComponent(origin)}`
        : baseLogoutUrl;
      const navigateWithExternal = url => {
        if (/^https?:\/\//.test(url)) {
          return navigateTo(url, { external: true });
        }
        return navigateTo(url);
      };
      await runLogoutFlow({
        logOutSSO,
        nextTick,
        navigateTo: navigateWithExternal,
        onReset: () => {
          resetAll();
          resetStorage();
        },
        onError: error => console.log('Logout error:', error),
        redirectPath,
      });
      console.log('Logout click: done');
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      signInWithSSO,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
